/**
 * ExportSheet — "take this book with you." The `.m4b` and the EPUB, §4.4 and §12's P6.
 *
 * ── The one thing this screen must not do ──────────────────────────────────
 * Offer a download that is quietly incomplete. A partly-narrated book **exports what exists** rather
 * than padding the holes with silence (`worker/src/export/m4b.ts` explains why at length), and the
 * whole justification for that choice is that the person is told *before* they tap. So the byte
 * count, the running time, and *"31 pages have no voice yet"* with the chapters named are on the
 * button, not in a report somebody reads afterwards. §5.8 already sets this standard for the offline
 * cache — *"'Download this book' shows the byte count **before** the tap (a 12-hour book is ~280 MB
 * — say the number)"* — and an audiobook file is the same promise with a longer memory: a file on a
 * phone outlives the tab it came from, which is also why the shortfall is written into the file's own
 * metadata and not only onto this screen.
 *
 * ── LAW 13 ─────────────────────────────────────────────────────────────────
 * This component renders nothing at all when the server does not offer it. It does not compute
 * `canExport` from a tier string or a build flag and it does not render a disabled button with a
 * tooltip: `GET /api/v1/downloads/:bookId` answers 403 `export-forbidden` for a reader who may read
 * and may not download, and the sheet says the server's own sentence. books shipped a button badged
 * *"Needs voice capture — Phase 6"* that stayed bolted shut against a feature that had landed in
 * Phase 6, because the claim was a prop rather than a check.
 *
 * ── Why the download is an `<a href>` and not a fetch ──────────────────────
 * A 280 MB `.m4b` pulled through `fetch` into a Blob is 280 MB of the phone's RAM before the first
 * byte reaches disk, and on iOS that is a tab the OS kills silently. A plain navigation hands the
 * transfer to the browser's own downloader, which streams it, resumes it (the route forwards
 * `Range`), and puts it in Files where the user expects it. The route is cookie-authorised for
 * exactly this reason — §6.1: *"you cannot set a header on an `<img>`, an `<audio>`, an
 * `<a download>`, or a link opened from iMessage"*.
 */

import { useCallback, useEffect, useState, type ReactElement } from "react";

import { ApiError, api } from "../lib/api.ts";

// ─── the wire ────────────────────────────────────────────────────────────────

export type ExportKind = "m4b" | "epub";
export type ExportState = "ready" | "stale" | "building" | "failed" | "none";

export interface ExportView {
  kind: ExportKind;
  state: ExportState;
  buildKey: string;
  bytes: number | null;
  builtAt: number | null;
  jobId: string | null;
  error: string | null;
  report: unknown;
  present: boolean | null;
}

export interface ExportGap {
  chapterOrd: number;
  chapterTitle: string;
  fromSid: string;
  pages: number;
}

export interface DownloadsManifest {
  bookId: string;
  editionId: string | null;
  state: "ready" | "importing";
  title?: string;
  author?: string | null;
  narration?: {
    pagesTotal: number;
    pagesNarrated: number;
    durationMs: number;
    gaps: ExportGap[];
    gapCount: number;
    voice: string | null;
  };
  text?: { chapters: number; images: number };
  exports: ExportView[];
}

export interface ExportSheetProps {
  bookId: string;
  /** Re-fetch when this changes — the book page bumps it after a narration lands. */
  refreshKey?: number;
}

/**
 * How often to re-ask while a build is running.
 *
 * An export is CPU on amd-server: an EPUB is a second or two, a 12-hour `.m4b` is under a minute of
 * stream copy. Three seconds is responsive without being a poll loop, and polling stops the moment
 * nothing is building — a screen left open on a desk must not talk to the server all night.
 */
const POLL_MS = 3_000;

export function ExportSheet(props: ExportSheetProps): ReactElement | null {
  const { bookId, refreshKey = 0 } = props;
  const [manifest, setManifest] = useState<DownloadsManifest | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState<ExportKind | null>(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(
    (signal?: AbortSignal) =>
      api
        .get<DownloadsManifest>(`/downloads/${encodeURIComponent(bookId)}`, signal ? { signal } : undefined)
        .then((m) => {
          setManifest(m);
          setForbidden(null);
          setError(null);
        })
        .catch((e: unknown) => {
          if (signal?.aborted) return;
          if (e instanceof ApiError && e.status === 403) {
            // The server's own sentence, not ours. LAW 13: one predicate, one explanation.
            setForbidden(e.message);
            setManifest(null);
            return;
          }
          if (e instanceof ApiError && e.status === 404) {
            // `readAccess` said no. Nothing to say — the book page above has already said it.
            setManifest(null);
            return;
          }
          setError(e instanceof ApiError ? e.message : "Couldn't ask about downloads.");
        }),
    [bookId],
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load, refreshKey, tick]);

  const building = manifest?.exports.some((e) => e.state === "building") ?? false;
  useEffect(() => {
    if (!building) return;
    const t = setTimeout(() => setTick((n) => n + 1), POLL_MS);
    return () => clearTimeout(t);
  }, [building, tick]);

  const build = useCallback(
    async (kind: ExportKind) => {
      setAsking(kind);
      setError(null);
      try {
        await api.post(`/downloads/${encodeURIComponent(bookId)}/${kind}`);
        setTick((n) => n + 1);
      } catch (e: unknown) {
        setError(e instanceof ApiError ? e.message : "Couldn't start the build.");
      } finally {
        setAsking(null);
      }
    },
    [bookId],
  );

  if (forbidden) {
    return (
      <section className="ab-panel">
        <h2 className="ab-h2">Take it with you</h2>
        <p className="ab-hint">{forbidden}</p>
      </section>
    );
  }
  if (!manifest) {
    return error ? (
      <section className="ab-panel">
        <h2 className="ab-h2">Take it with you</h2>
        <p className="ab-hint ab-hint-bad">{error}</p>
      </section>
    ) : null;
  }
  if (manifest.state === "importing") {
    return (
      <section className="ab-panel">
        <h2 className="ab-h2">Take it with you</h2>
        <p className="ab-hint">This book is still being imported. Downloads appear once it has finished.</p>
      </section>
    );
  }

  const n = manifest.narration;
  const complete = n ? n.pagesNarrated >= n.pagesTotal : false;

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Take it with you</h2>
      <p className="ab-lede">
        Two files, built from what is already here. Both are rebuilt whenever the book changes, and
        both can be cleared to make room — asking again rebuilds them.
      </p>

      {error ? <p className="ab-hint ab-hint-bad">{error}</p> : null}

      {manifest.exports.map((view) => (
        <Row
          key={view.kind}
          bookId={bookId}
          view={view}
          narration={n ?? null}
          complete={complete}
          chapters={manifest.text?.chapters ?? 0}
          busy={asking === view.kind}
          onBuild={() => void build(view.kind)}
        />
      ))}
    </section>
  );
}

// ─── one row ─────────────────────────────────────────────────────────────────

interface RowProps {
  bookId: string;
  view: ExportView;
  narration: DownloadsManifest["narration"] | null;
  complete: boolean;
  chapters: number;
  busy: boolean;
  onBuild(): void;
}

function Row(props: RowProps): ReactElement {
  const { bookId, view, narration, complete, chapters, busy, onBuild } = props;
  const audio = view.kind === "m4b";
  const nothingToRead = audio && (narration?.pagesNarrated ?? 0) === 0;

  return (
    <div className="ab-confirm-row">
      <div className="ab-confirm-text">
        <strong>{audio ? "Audiobook (.m4b)" : "EPUB"}</strong>
        <p className="ab-hint">
          {audio
            ? "One file with real chapter marks, cover art and the voice you hear here. Apple Books, " +
              "Bookplayer and a car stereo all treat it as an audiobook — it remembers where you stopped."
            : `The book itself, reflowable, with its ${chapters} ${chapters === 1 ? "chapter" : "chapters"} ` +
              "and every sentence still addressable. No read-along: that needs Media Overlays, which are " +
              "deliberately not in this version."}
        </p>

        {/* The disclosure, before the tap. */}
        {audio && narration ? (
          nothingToRead ? (
            <p className="ab-hint ab-hint-quiet">
              None of this book has been narrated yet. Press play on a chapter — every page you listen
              to becomes part of the audiobook.
            </p>
          ) : (
            <p className={complete ? "ab-hint ab-hint-quiet" : "ab-hint ab-hint-bad"}>
              {complete
                ? `The whole book — ${runtime(narration.durationMs)} in ${narration.voice ?? "Karti"}'s voice.`
                : `${runtime(narration.durationMs)} of ${narration.pagesNarrated} narrated ` +
                  `${narration.pagesNarrated === 1 ? "page" : "pages"} out of ${narration.pagesTotal}. ` +
                  `The ${narration.pagesTotal - narration.pagesNarrated} without a voice are LEFT OUT, ` +
                  `not silent — the file jumps straight past them.`}
            </p>
          )
        ) : null}

        {audio && narration && narration.gaps.length > 0 ? (
          <p className="ab-hint ab-hint-quiet">
            Not yet narrated: {gapNames(narration)}
          </p>
        ) : null}

        {view.state === "stale" ? (
          <p className="ab-hint ab-hint-quiet">
            The file you can download now was built before the book changed
            {view.builtAt ? ` (${when(view.builtAt)})` : ""}. Rebuild it to include what is new.
          </p>
        ) : null}
        {view.state === "failed" && view.error ? <p className="ab-hint ab-hint-bad">{view.error}</p> : null}
        {view.present === null && view.state === "ready" ? (
          <p className="ab-hint ab-hint-quiet">
            The storage machine did not answer, so this file could not be confirmed. The link may fail.
          </p>
        ) : null}
      </div>

      <div className="ab-actions">
        {(view.state === "ready" || view.state === "stale") && view.present !== false ? (
          <a
            className="ab-btn ab-btn-primary"
            /* An absolute path, and no `download` attribute: the route sets `Content-Disposition`
               with both the ASCII and the RFC 5987 filename, so the browser gets the book's real
               title even in Devanagari. A `download` attribute here would override it with the URL's
               last segment — "m4b". */
            href={`/api/v1/downloads/${encodeURIComponent(bookId)}/${view.kind}`}
          >
            Download{view.bytes !== null ? ` · ${bytes(view.bytes)}` : ""}
          </a>
        ) : null}

        {view.state === "building" ? (
          <span className="ab-hint">
            <span className="ab-spinner" aria-hidden /> Building…
          </span>
        ) : view.state === "ready" ? (
          /**
           * ⚠️ No button. A `ready` export is one whose `exportBuildKey` matches the book as it is
           * right now — the title, the author, the cover, the chapter list, and (for the `.m4b`) every
           * page's `artifactSha`. There is nothing to rebuild, and the route says so: `POST` on a
           * ready export returns `{state:"ready", jobId:null}` without enqueueing anything.
           *
           * It used to render *Rebuild* here anyway. Pressing it POSTed, got that 200 back, bumped
           * `tick`, refetched, and re-rendered identically — no job, no message, no change, forever.
           * A control that cannot do anything is worse than no control: the reader presses it twice
           * and concludes the export is broken. When something really does change, the key changes
           * with it and this becomes `stale`, which has a button that works.
           */
          <span className="ab-hint">Up to date with the book as it is now.</span>
        ) : (
          <button type="button" className="ab-btn ab-btn-primary" onClick={onBuild} disabled={busy || nothingToRead}>
            {view.state === "stale" ? "Rebuild with the new pages" : "Build it"}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── copy ────────────────────────────────────────────────────────────────────

/**
 * Bytes, in the unit a person uses.
 *
 * Decimal MB/GB, not MiB: that is what the phone's storage screen says, what the App Store says, and
 * what the number in §5.8's "a 12-hour book is ~280 MB" is. Being technically right in binary units
 * makes the sheet disagree with the device it is running on.
 */
export function bytes(n: number): string {
  if (n < 1_000) return `${n} B`;
  if (n < 1_000_000) return `${Math.round(n / 1_000)} KB`;
  if (n < 1_000_000_000) return `${(n / 1_000_000).toFixed(n < 10_000_000 ? 1 : 0)} MB`;
  return `${(n / 1_000_000_000).toFixed(2)} GB`;
}

/** `9 hr 14 min`, `14 min`, `40 sec`. An audiobook's length is the first thing anybody asks. */
export function runtime(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total} sec`;
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  if (hours === 0) return `${minutes} min`;
  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
}

function when(at: number): string {
  const days = Math.floor((Date.now() - at) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

/**
 * The chapters with holes, by name.
 *
 * Named, not counted — the same rule the builder's refusals follow. "31 pages missing" sends
 * somebody hunting through a book; "Chapter 12, Chapter 13 and 4 more" is somewhere to press play.
 */
function gapNames(narration: NonNullable<DownloadsManifest["narration"]>): string {
  const titled = narration.gaps.map((g) => g.chapterTitle || `Chapter ${g.chapterOrd + 1}`);
  const unique = [...new Set(titled)];
  const shown = unique.slice(0, 3);
  const rest = narration.gapCount - shown.length;
  return `${shown.join(", ")}${rest > 0 ? `, and ${rest} more` : ""}.`;
}
